/**
 * IPC 通信类型定义
 * Tauri IPC 命令与事件的类型约束
 */
import type { UserInfo } from '@/types/api'

/** IPC 命令名称（snake_case，对齐 Rust 端） */
export enum IpcCommand {
  /** 通用 API 代理：所有业务 API 通过 Rust 转发，避免 WebView CORS */
  ApiProxy = 'api_proxy',
  /** 获取 Rust 运行时实际使用的 Boss API 地址 */
  ApiBaseUrl = 'api_base_url',
  /** 获取私有化安装包 Endpoint Profile 状态 */
  EndpointProfileInfo = 'endpoint_profile_info',
  /** 通用文件下载代理：GET 下载流通过 Rust 注入 x-token，避免 WebView CORS/鉴权丢失 */
  ApiDownload = 'api_download',
  /** 保存前端生成的文本文件到系统下载目录 */
  SaveDownloadText = 'save_download_text',
  /** 保存前端生成的二进制文件到系统下载目录 */
  SaveDownloadBase64 = 'save_download_base64',
  /** 通用文件上传代理：multipart 文件通过 Rust 注入 x-token，避免 WebView CORS/鉴权丢失 */
  ApiUpload = 'api_upload',
  /** 通用文件上传代理：Tauri 原生拖拽路径读盘后 multipart 上传 */
  ApiUploadByPath = 'api_upload_by_path',
  /** 系统通知：macOS 使用客户端 logo identity image，其他平台回退插件通知 */
  SystemNotificationSend = 'system_notification_send',
  /** 调起当前桌面 WebView 的原生系统打印窗口 */
  PrintCurrentWebview = 'print_current_webview',
  AuthLogin = 'auth_login',
  AuthLogout = 'auth_logout',
  AuthGetToken = 'auth_get_token',
  AuthSyncToken = 'auth_sync_token',
  AuthHandleTokenExpired = 'auth_handle_token_expired',
  AuthRefreshToken = 'auth_refresh_token',
  CacheGet = 'cache_get',
  CacheSet = 'cache_set',
  CacheClear = 'cache_clear',
  AiStreamStart = 'ai_stream_start',
  AiStreamStop = 'ai_stream_stop',
  /** Relay 模型流由桌面 Rust 发起，WebView 只接收流数据 */
  RelayModelStream = 'relay_model_stream',
  /** Relay 非流式兜底请求由桌面 Rust 发起 */
  RelayModelComplete = 'relay_model_complete',
  /** 取消指定 Relay 流式或非流式请求 */
  RelayModelRequestStop = 'relay_model_request_stop',
  AnalysisJobCreate = 'analysis_job_create',
  AnalysisJobUpdateStatus = 'analysis_job_update_status',
  AnalysisJobListPending = 'analysis_job_list_pending',
  AnalysisJobRecoverRunning = 'analysis_job_recover_running',
  AnalysisJobCountStartedSince = 'analysis_job_count_started_since',
  AnalysisJobCancel = 'analysis_job_cancel',
  AnalysisJobMarkRetry = 'analysis_job_mark_retry',
  AnalysisSyncEnqueue = 'analysis_sync_enqueue',
  AnalysisSyncListPending = 'analysis_sync_list_pending',
  AnalysisSyncMarkSyncing = 'analysis_sync_mark_syncing',
  AnalysisSyncMarkAccepted = 'analysis_sync_mark_accepted',
  AnalysisSyncMarkRetryable = 'analysis_sync_mark_retryable',
  AnalysisSyncMarkFailed = 'analysis_sync_mark_failed',
  AnalysisSyncManualRetry = 'analysis_sync_manual_retry',
  AnalysisSyncRecoverSyncing = 'analysis_sync_recover_syncing',
  AnalysisSyncReconcileAcceptedJobs = 'analysis_sync_reconcile_accepted_jobs',
  AnalysisSyncGetLatestLocalResult = 'analysis_sync_get_latest_local_result',
  AnalysisSyncUpsertAcceptedSnapshot = 'analysis_sync_upsert_accepted_snapshot',
  AnalysisSyncCleanupScope = 'analysis_sync_cleanup_scope',
  ChatWindowUploadEnqueue = 'chat_window_upload_enqueue',
  ChatWindowUploadListDue = 'chat_window_upload_list_due',
  ChatWindowUploadMarkSyncing = 'chat_window_upload_mark_syncing',
  ChatWindowUploadMarkAccepted = 'chat_window_upload_mark_accepted',
  ChatWindowUploadMarkRetryable = 'chat_window_upload_mark_retryable',
  ChatWindowUploadRefreshTokenRetryable = 'chat_window_upload_refresh_token_retryable',
  ChatWindowUploadMarkFailed = 'chat_window_upload_mark_failed',
  ChatWindowUploadRecoverSyncing = 'chat_window_upload_recover_syncing',
  ChatWindowMediaUpload = 'chat_window_media_upload',
  AnalysisWindowLocalSave = 'analysis_window_local_save',
  AnalysisWindowLocalGet = 'analysis_window_local_get',
  AnalysisWindowLocalDelete = 'analysis_window_local_delete',
  TaskChatWindowLocalSave = 'task_chat_window_local_save',
  TaskChatWindowLocalGet = 'task_chat_window_local_get',
  TaskChatWindowLocalCleanup = 'task_chat_window_local_cleanup',
  ChatServiceSync = 'chat_service_sync',
  ChatGetContacts = 'chat_get_contacts',
  ChatGetActiveSessions = 'chat_get_active_sessions',
  ChatGetContactsForImport = 'chat_get_contacts_for_import',
  ChatGetFriendDetections = 'chat_get_friend_detections',
  ChatGetMessages = 'chat_get_messages',
  ChatGetServiceStatus = 'chat_get_service_status',
  ChatTriggerSync = 'chat_trigger_sync',
  ChatUploadDiagnosticsStatus = 'chat_upload_diagnostics_status',
  ChatUploadDiagnosticsControl = 'chat_upload_diagnostics_control',
  ChatGetActiveSession = 'chat_get_active_session',
  ChatClearActiveSessionPocCache = 'chat_clear_active_session_poc_cache',
  ChatGetMedia = 'chat_get_media',
  ChatVerifyContactNickname = 'chat_verify_contact_nickname',
  ChatUpdateContactAlias = 'chat_update_contact_alias',
  ChatSuggestContactByNickname = 'chat_suggest_contact_by_nickname',
  ChatSuggestContactsByNickname = 'chat_suggest_contacts_by_nickname',
  ChatRankContactsByNickname = 'chat_rank_contacts_by_nickname',
  /** Epic 11 2.3: 朋友圈 API */
  ChatGetSnsFeeds = 'chat_get_sns_feeds',
  ChatFindContactsByName = 'chat_find_contacts_by_name',
  ChatFindAccountByRealWechatId = 'chat_find_account_by_real_wechat_id',
  ApiChatSummary = 'api_chat_summary',
  LogBatchUpload = 'log_batch_upload',
  LogReportCrash = 'log_report_crash',
  LogDismissCrash = 'log_dismiss_crash',
  LogCheckCrash = 'log_check_crash',
  SidebarClose = 'sidebar_close',
  SidebarSyncGeometry = 'sidebar_sync_geometry',
  UpdateCheck = 'update_check',
  UpdateInstall = 'update_install',
  /** 知识库：文档上传（前端→Rust，Rust本地读取+multipart POST Boss） */
  KnowledgeDocUpload = 'knowledge_doc_upload',
  /** 知识库：按本地路径上传（Tauri OS 层拖拽专用，绕开 NSOpenPanel assertion） */
  KnowledgeDocUploadByPath = 'knowledge_doc_upload_by_path',
  /** 知识库：获取文档列表（本地SQLite） */
  KnowledgeDocList = 'knowledge_doc_list',
  /** 知识库：触发AI学习（SSE异步） */
  KnowledgeDocLearn = 'knowledge_doc_learn',
  /** 知识库：删除文档 */
  KnowledgeDocDelete = 'knowledge_doc_delete',
  /** 品牌配置：获取（本地SQLite） */
  BrandConfigGet = 'brand_config_get',
  /** 品牌配置：从Boss同步 */
  BrandConfigSync = 'brand_config_sync',
  /** 建议回复润色：启动 SSE 流式润色 */
  AiReplyPolishStart = 'ai_reply_polish_start',
  /** 建议回复润色：取消指定/全部润色会话 */
  AiReplyPolishStop = 'ai_reply_polish_stop',
  /** 建议回复真实发送确认 pending 原文加密 */
  ReplyPendingEncrypt = 'reply_pending_encrypt',
  /** 建议回复真实发送确认 pending 原文解密 */
  ReplyPendingDecrypt = 'reply_pending_decrypt',
  /** wxid_alias: 校验所有 alias 记录类型一致性 */
  WxidAliasVerifyAll = 'wxid_alias_verify_all',
  /** wxid_alias: 按 OCR 名称查询候选 */
  WxidAliasLookup = 'wxid_alias_lookup',
  /** wxid_alias: 删除一条 alias 绑定 */
  WxidAliasDelete = 'wxid_alias_delete',
  /** 多微信账号：拉取已解密账号列表（feat/multi-wechat-switch） */
  ChatListAccounts = 'chat_list_accounts',
  /** 多微信账号：切换 active 账号（后端重建 Service） */
  ChatSwitchAccount = 'chat_switch_account',
  /** 退出应用（完全退出进程） */
  AppExit = 'app_exit',
  /** 设备版本号主动上报（登录后、connector 版本变化等即时场景） */
  ReportVersion = 'report_version',
  /** 设备标识：获取当前设备 ID */
  DeviceGetId = 'device_get_id',
  /** WeChat OCR：查询标题栏 crop 配置 */
  WechatOcrCropGetConfig = 'wechat_ocr_crop_get_config',
  /** WeChat OCR：设置标题栏 crop offset */
  WechatOcrCropSetConfig = 'wechat_ocr_crop_set_config',
  /** WeChat OCR：重置标题栏 crop offset */
  WechatOcrCropResetConfig = 'wechat_ocr_crop_reset_config',
  /** WeChat OCR：生成标题栏 crop 调试截图 */
  WechatOcrCropCaptureDebug = 'wechat_ocr_crop_capture_debug',
}

/** Tauri 事件名称（domain:entity:action 格式） */
export enum TauriEvent {
  AiStreamChunk = 'ai:stream:chunk',
  AiStreamProcessed = 'ai:stream:processed',
  AiStreamError = 'ai:stream:error',
  /** D1: SSE 连接重试通知 */
  AiStreamRetry = 'ai:stream:retry',
  ChatMessageNew = 'chat:message:new',
  ChatStatusChanged = 'chat:status:changed',
  ChatSyncProgress = 'chat:sync:progress',
  ChatSyncComplete = 'chat:sync:complete',
  AuthTokenExpired = 'auth:token:expired',
  AuthTokenRefreshed = 'auth:token:refreshed',
  AuthSessionChanged = 'auth:session:changed',
  NetworkStatusChanged = 'network:status:changed',
  UpdateAvailable = 'update:available',
  UpdateCleared = 'update:cleared',
  UpdateDownloadProgress = 'update:download:progress',
  AppCrashDetected = 'app:crash:detected',
  AiStreamTasksCreated = 'ai:stream:tasks_created',
  AiStreamTasksCompleted = 'ai:stream:tasks_completed',
  /** Story 18.6: 知识库匹配完成通知（后端化匹配） */
  AiStreamKnowledgeMatched = 'ai:stream:knowledge_matched',
  /** AI 档案提取完成通知（Story 20.3/20.4） */
  AiStreamProfileUpdated = 'ai:stream:profile_updated',
  /** AI 自动判别 BD 阶段通知（后端在 SSE 主流程推送） */
  AiStreamBdStageUpdated = 'ai:stream:bd_stage_updated',
  AnalysisUpdated = 'analysis:updated',
  AutoAnalysisConfigUpdated = 'auto-analysis:config-updated',
  CustomerAvatarUpdated = 'customer:avatar:updated',
  CustomerBdRoleUpdated = 'customer:bd-role:updated',
  /** 微信是否在前台（前台=true 时显示内容；后台=false 时清空显示） */
  SidebarWechatActiveChanged = 'sidebar:wechat-active-changed',
  /** ChatService 连通状态（每 5s 心跳 / sidebar 打开时立即下发首次状态） */
  SidebarChatServiceStatus = 'sidebar:chatservice-status',
  /** 多微信账号切换：前台微信窗口 wxid 变化（feat/multi-wechat-switch） */
  SidebarWechatAccountChanged = 'sidebar:wechat-account-changed',
  /** 知识库：文档学习进度推送 */
  KnowledgeDocLearnProgress = 'knowledge:doc:learn:progress',
  /** 建议回复润色：流式文本块 */
  AiReplyPolishChunk = 'ai:reply-polish:chunk',
  /** 建议回复润色：完成（携带完整最终文本） */
  AiReplyPolishDone = 'ai:reply-polish:done',
  /** 建议回复润色：错误 */
  AiReplyPolishError = 'ai:reply-polish:error',
  /**
   * 客户阶段变更事件（用于线索唤醒 deal_closed 条件检测）
   * payload: { customerId: number, stage: number }
   */
  CustomerStageChanged = 'customer:stage:changed',
  /**
   * 客户标签变更事件（侧边栏同步人工/AI 标签后广播）
   * 订阅方：客户管理列表、客户详情页、侧边栏等需要刷新标签展示的页面
   * payload: { customerId: number, tagSource: 0 | 1 }
   */
  CustomerTagsUpdated = 'customer:tags:updated',
  /**
   * 客户 BD 阶段变更事件（侧边栏 AI 数据分析自动判别后广播）
   * 订阅方：客户详情页（命中当前打开的客户时即时刷新展示）
   * payload: { customerId: number, bdStage: BdStageKey, bdStageName: string }
   */
  CustomerBdStageChanged = 'customer:bd-stage:changed',
}

/** Tauri Event payload 类型映射 */
export interface TauriEventPayloadMap {
  [TauriEvent.AiStreamChunk]: {
    sessionId: string
    content: string
    done: boolean
    eventType?: string
    reason?: string
    attempt?: number
  }
  [TauriEvent.AiStreamProcessed]: { sessionId: string; content: string; done: boolean }
  [TauriEvent.AiStreamError]: { sessionId: string; error: string }
  [TauriEvent.AiStreamRetry]: {
    sessionId: string
    attempt: number
    maxRetries: number
    error: string
  }
  [TauriEvent.ChatMessageNew]: {
    contactId: string
    wechatId?: string
    wechatPid?: number
    message: ChatMessage
  }
  [TauriEvent.ChatStatusChanged]: ChatServiceStatus
  [TauriEvent.ChatSyncProgress]: ChatSyncStatus
  [TauriEvent.ChatSyncComplete]: { count: number; lastSyncTime: string }
  [TauriEvent.AuthTokenExpired]: Record<string, never>
  [TauriEvent.AuthTokenRefreshed]: { token: string }
  [TauriEvent.AuthSessionChanged]: {
    loggedIn: boolean
    token: string
    user: UserInfo | null
    permissions?: string[]
    source: string
  }
  [TauriEvent.NetworkStatusChanged]: { online: boolean }
  [TauriEvent.UpdateAvailable]: {
    version: string
    releaseNotes: string
    pubDate: string
    isForced?: boolean
  }
  [TauriEvent.UpdateCleared]: Record<string, never>
  [TauriEvent.UpdateDownloadProgress]: { percent: number; downloaded: number; total: number }
  [TauriEvent.AppCrashDetected]: { message: string; timestamp: string }
  [TauriEvent.AiStreamTasksCreated]: {
    count: number
    taskIds: number[]
    customerName: string
  }
  [TauriEvent.AiStreamTasksCompleted]: {
    count: number
    taskIds: number[]
    skipped: number
  }
  [TauriEvent.AiStreamKnowledgeMatched]: {
    sessionId: string
    traceId: string
    docs: Array<{
      docId: number
      docName: string
      matchedKeywords: string[]
      score: number
    }>
  }
  [TauriEvent.AiStreamProfileUpdated]: {
    customerId: number
    phone: string
    region: string
    regionRaw?: string
    regionSource?: 'sync' | 'manual'
    position: string
    industry: string
    jobTitle: string
    painPoints: string
    competitors: string
    shippingAddress?: string
    aiProfileVersion: number
    emotionScore?: number
    emotionLevel?: 'positive' | 'hesitant' | 'anxious' | 'resistant'
    emotionLabel?: string
    emotionReason?: string
    emotionUpdatedAt?: string
  }
  [TauriEvent.AiStreamBdStageUpdated]: {
    traceId: string
    customerId: number
    bdStage: BdStageKey
    bdStageName: string
  }
  [TauriEvent.AnalysisUpdated]: { customerId: number }
  [TauriEvent.AutoAnalysisConfigUpdated]: {
    enabled: boolean
    singleCustomerNewMessageCount?: number
    mutualTotalMessageCount?: number
    allowGroupChat?: boolean
  }
  [TauriEvent.CustomerAvatarUpdated]: { customerId: number }
  [TauriEvent.CustomerBdRoleUpdated]: { customerId: number }
  [TauriEvent.SidebarWechatActiveChanged]: { active: boolean }
  [TauriEvent.SidebarChatServiceStatus]: {
    connected: boolean
    /** ChatService 密钥是否已就绪（false = 密钥失效/换号，需提示用户重新破解） */
    ready?: boolean
    /** true 表示本次状态为最近成功快照/瞬时失败兜底，不应覆盖连接横幅状态 */
    status_stale?: boolean
    wechat_id?: string | null
    wechat_pid?: number | null
  }
  [TauriEvent.SidebarWechatAccountChanged]: {
    wxid: string
    pid: number
    matchedAccount: boolean
  }
  [TauriEvent.KnowledgeDocLearnProgress]: LearnProgressEvent
  [TauriEvent.AiReplyPolishChunk]: { sessionId: string; text: string }
  [TauriEvent.AiReplyPolishDone]: { sessionId: string; fullText: string }
  [TauriEvent.AiReplyPolishError]: { sessionId: string; error: string }
  [TauriEvent.CustomerStageChanged]: { customerId: number; stage: number }
  [TauriEvent.CustomerTagsUpdated]: { customerId: number; tagSource: 0 | 1 }
  [TauriEvent.CustomerBdStageChanged]: {
    customerId: number
    bdStage: BdStageKey
    bdStageName: string
  }
}

/** wxId-昵称验证响应（chat_verify_contact_nickname） */
export interface VerifyContactNicknameResponse {
  found: boolean
  actualNickname: string
  matched: boolean
  avatar: string
  /** 用户自定义微信号 (alias)，如 PLA-Fly；未设置时为空串 */
  alias: string
  /** 微信好友通过时间，ChatService 原始文本 */
  friendAcceptTime?: string
  /** 微信好友通过时间戳 */
  friendAcceptTimestamp?: number
  /** 微信好友添加来源场景 */
  friendSourceScene?: number
  /** 微信好友添加来源名称 */
  friendSourceName?: string
  /** 微信地区：国家 */
  regionCountry?: string
  /** 微信地区：省份 */
  regionProvince?: string
  /** 微信地区：城市 */
  regionCity?: string
  /** 微信地区展示名 */
  regionName?: string
}

/** 私有化安装包 Endpoint Profile 状态（不包含签名密钥或完整配置文件） */
export interface EndpointProfileInfo {
  present: boolean
  valid: boolean
  companyCode?: string | null
  apiBase?: string | null
  connectorBase?: string | null
  updaterBase?: string | null
  appVersion?: string | null
  channel?: string | null
  errorCode?: string | null
  message?: string | null
}

/** 活跃会话响应（chat_get_active_session） */
export interface ActiveSessionResponse {
  wxId: string
  nickname: string
  /** 当前 wxId 的身份来源；window/contact_exact 可自动匹配客户，lookup/nickname_only 只能展示或人工确认 */
  identitySource?: 'window' | 'contact_exact' | 'lookup' | 'nickname_only'
  /** 本次窗口标题识别来源，如 paddle-ocr / wechat-ocr / uia-fallback */
  detectionSource?: string
  /** 本地 OCR/UIA 直接识别到的原始窗口昵称；nickname 可能是 alias/DB 展示名 */
  rawNickname?: string
  /** 本次重复候选计数实际使用的查询名，供确认 picker 使用 */
  matchedLookupName?: string
  /** 本次重复候选计数实际使用的类型过滤 */
  matchedLookupType?: 'personal' | 'openim' | 'official' | 'chatroom'
  hasActiveSession: boolean
  isGroup: boolean
  /** OCR 信号推断类型与 lookup 命中类型不匹配 */
  typeMismatch?: boolean
  /** typeMismatch=true 时推荐的正确类型候选 wxId（用于 picker 预选） */
  suggestedWxId?: string
  /** ChatService 后端是否可达（来自心跳缓存）
   *  用于区分两种识别失败：
   *  - chatServiceAvailable=true  + wxId="" → 真识别失败/无对应客户
   *  - chatServiceAvailable=false           → 后端未启动，应引导用户启动 ChatService
   */
  chatServiceAvailable?: boolean
  /** macOS 专用：屏幕上的微信窗口和 ChatService 当前账号不一致 */
  accountMismatch?: boolean
  /** 屏幕上当前微信窗口 PID */
  localWechatPid?: number | null
  /** ChatService 当前微信账号 PID */
  chatServiceWechatPid?: number | null
  /** 2026-05-30: 同 nickname/remark 的本地候选总数（>=2 表示重复备注）
   *  前端据此显示「检测到 N 个同名联系人」横幅引导用户确认 */
  duplicateCount?: number
  /** 2026-05-30: 是否模糊匹配命中（is_high_confidence=false）
   *  true 时前端显示「识别结果可能有误」横幅 */
  lowConfidence?: boolean
  /** 低置信命中的候选 wxId，仅供用户点击横幅后确认，不代表已确认识别成功 */
  lowConfidenceCandidateWxId?: string
}

/** 客户匹配状态 */
export type MatchStatus = 'matched' | 'matching' | 'unmatched' | 'none'

/** IPC 调用结果 */
export interface IPCResult<T = unknown> {
  success: boolean
  data?: T
  error?: string
}
